import './styles.css';
import { api, type SessionState } from './api.ts';
import { renderGate } from './gate.ts';
import { DeckViewer, type Manifest } from './deck.ts';

const root = document.getElementById('app');
if (!root) throw new Error('#app missing');

let viewer: DeckViewer | null = null;

function boot(text: string): void {
  const d = document.createElement('div');
  d.className = 'boot';
  d.textContent = text;
  root!.replaceChildren(d);
}

function toast(text: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.setAttribute('role', 'status');
  t.textContent = text;
  document.body.appendChild(t);
  window.setTimeout(() => t.remove(), 4200);
}

/**
 * `?v=N` asks for a specific version. It is honoured only for a signed-in
 * admin — the server serves the published deck to everyone else regardless, so
 * this is a request, not an instruction, and passing it on is safe.
 */
function requestedVersion(): string {
  const raw = new URLSearchParams(window.location.search).get('v') ?? '';
  return /^\d{1,4}$/.test(raw) ? raw : '';
}

/**
 * A persistent marker that this is a preview, and whether it is what viewers
 * actually see.
 *
 * It used to say "Not published" on every preview, including a preview of the
 * live version — so the one banner an admin most needs to trust said the
 * opposite of the truth. The published version is a pointer on the deck record
 * and a manifest only knows its own number, so the answer is sent alongside it.
 *
 * Colour carries the same fact as the words: orange for something viewers
 * cannot see, green when the preview IS the live deck.
 */
function showPreviewBanner(version: number, publishedVersion: number | null | undefined): void {
  document.getElementById('preview-banner')?.remove();
  // Absent means the server was not asked (not an admin preview) and null means
  // nothing is published — neither is "this version is live", so both fall
  // through to the unpublished wording rather than being treated as a match.
  const isLive = typeof publishedVersion === 'number' && publishedVersion === version;
  const el = document.createElement('div');
  el.id = 'preview-banner';
  el.textContent = isLive
    ? `Preview — version ${version}. Currently published.`
    : `Preview — version ${version}. Not published.`;
  el.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'z-index:2147483647',
    `background:${isLive ? '#1d6b47' : '#b4610f'}`,
    'color:#fff',
    'font:600 12px/1 system-ui,-apple-system,Segoe UI,sans-serif',
    'letter-spacing:.04em',
    'text-align:center',
    'padding:7px 12px',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(el);
}

async function loadDeck(): Promise<void> {
  boot('Loading presentation…');
  const v = requestedVersion();
  // Through the api module, so this cannot drift from the deck the rest of
  // the page is talking to — which is exactly what happened when it was a raw
  // fetch: the session resolved against the right deck and the manifest did not.
  const res = await api.deck(v || undefined);
  if (res.status === 401 || res.status === 403) {
    await showGate();
    return;
  }
  if (!res.ok) {
    boot('The presentation could not be loaded. Please refresh.');
    return;
  }
  const manifest = (await res.json()) as Manifest;
  document.title = manifest.subtitle ? `${manifest.title} — ${manifest.subtitle}` : manifest.title;

  // The server says whether this is a preview; the client never decides.
  //
  // It used to infer one from "the version I got back matches the one I asked
  // for", which is also true for a viewer who asks for the published version —
  // so an investor adding ?v= to a link saw a preview banner. Only the server
  // knows whether it granted a preview.
  if (manifest.preview) {
    showPreviewBanner(manifest.preview.version, manifest.preview.publishedVersion);
  }
  viewer?.destroy();
  viewer = new DeckViewer(manifest, root!, async () => {
    await api.logout();
    viewer?.destroy();
    viewer = null;
    toast('Signed out.');
    await showGate();
  });
}

/**
 * Render whatever this viewer needs to do next.
 *
 * A route that names no deck still gets a page — the request-access form —
 * rather than an error, because someone with a mistyped or stale link is
 * exactly the person we want to hear from.
 */
async function showGate(state?: SessionState): Promise<void> {
  const s = state ?? (await api.session());
  if (s.stage === 'ready' && !s.unknownDeck) {
    await loadDeck();
    return;
  }

  // The site key lives on the deck's own session response, which a route with
  // no deck does not get — fetch it separately so the request form is still
  // captcha-protected there.
  const siteKey = s.recaptchaSiteKey ?? (s.unknownDeck ? (await api.siteConfig()).recaptchaSiteKey : undefined);

  renderGate(root!, s.stage, {
    deckTitle: s.title ?? 'PurposeBook presentation',
    recaptchaSiteKey: siteKey,
    access: s.access,
    unknownDeck: s.unknownDeck,
    onReady: () => {
      void loadDeck();
    },
  });
}

async function start(): Promise<void> {
  try {
    await showGate();
  } catch {
    boot('Cannot reach the server. Please check your connection and refresh.');
  }
}

void start();
